import { Button } from "@/lib/components/Button/Button";
import HorizontalList from "@/lib/components/HorizontalList";
import Toast from "@/lib/components/Toast";
import { cn } from "@/lib/utils";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  IconCheck,
  IconChevronLeft,
  IconExclamationCircle,
  IconLibrary,
  IconLoader2,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import GradientPromptTextarea from "./GradientPromptTextarea";
import {
  GoBack,
  MainViewState,
  SetView,
  UpdateCurrentView,
  useViewTransitionProps,
  ViewStateOf,
} from "./creative-project-hooks";
import { isSourceImageProcessing } from "@repo/api/utils/composition";
import { SourceImageDto } from "@repo/api/source-image";
import { CreativeElementDto } from "@repo/api/creative-element";
import { queryClient } from "@/lib/queries/query-client";
import { sourceImagePresetQueryKeys } from "@/lib/queries/source-image-preset-queries";
import { useCallback, useMemo, useState } from "react";
import { motion } from "motion/react";
import ImagePreview from "./ImagePreview";
import { ActionIdentifier } from "@/lib/stores/editor-store/creative-project-state";
import { emitInspirationAction } from "./Inspiration/inspiration-action-events";
import { calculateCreativeImageEditCredits } from "@repo/api/utils/billing";
import { useTeamQuery } from "@/lib/queries/team-queries";
import CreditsText from "@/features/Billing/CreditsText";
import { AnalyticsApi } from "@/lib/api/analytics-api";

function EditImageView({
  mainView,
  setMainView,
  goBackMainView,
  updateCurrentMainView,
}: {
  mainView: ViewStateOf<MainViewState, "EDIT_IMAGE">;
  setMainView: SetView<MainViewState>;
  goBackMainView: GoBack;
  updateCurrentMainView: UpdateCurrentView<MainViewState>;
}) {
  const transitionProps = useViewTransitionProps();

  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const sourceImagesFromEditorStore = useEditorStore(
    (state) => state.sourceImages,
  );

  const handleGoBack = useCallback(() => {
    emitInspirationAction(ActionIdentifier.NAVIGATE_ANIMATION);
    goBackMainView("SLIDE_FROM_LEFT");
  }, [goBackMainView]);

  const sourceImagesFromTimelineItem = timelineItem?.sourceImageExternalIds
    ? timelineItem.sourceImageExternalIds
        .map((externalId) => sourceImagesFromEditorStore[externalId])
        .filter(
          (item): item is SourceImageDto =>
            !!item && !item.deletedAt && !!item.src,
        )
    : [];

  const isSelectionConfirmed =
    mainView.isSelectionConfirmed ?? sourceImagesFromTimelineItem.length <= 1;
  const [showSelectionError, setShowSelectionError] = useState(false);

  const setIsSelectionConfirmed = useCallback(
    (value: boolean) => {
      updateCurrentMainView((view) => {
        return { ...view, isSelectionConfirmed: value };
      });
    },
    [updateCurrentMainView],
  );

  const confirmSelection = useCallback(() => {
    setIsSelectionConfirmed(true);
    setShowSelectionError(false);
  }, [setIsSelectionConfirmed]);

  const imagePreviewPrimaryAction = useMemo(() => {
    return {
      label: "Use for animation",
      icon: <IconCheck width={24} height={24} strokeWidth={2.5} />,
      onClick: handleGoBack,
    };
  }, [handleGoBack]);

  const isSelectionPending = !isSelectionConfirmed;

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center overflow-hidden"
      {...transitionProps}
    >
      <div className="flex w-full max-w-212 flex-1 flex-col overflow-hidden p-8">
        <div className="h-7">
          <button
            className="flex cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-text-primary"
            onClick={handleGoBack}
          >
            <IconChevronLeft
              className="text-text-primary"
              width={16}
              height={16}
            />
            Edit Image
          </button>
        </div>
        <ImagePreview
          sourceImageKey={mainView.sourceImageKey}
          primaryAction={imagePreviewPrimaryAction}
          showRemoveOption={false}
          onImageClick={confirmSelection}
          onImageReplaced={confirmSelection}
        />
        <SourceImageSelector
          mainView={mainView}
          sourceImagesFromTimelineItem={sourceImagesFromTimelineItem}
          onSelect={confirmSelection}
          isSelectionPending={isSelectionPending}
        />
        <div
          id="edit-image-selection-error"
          role="alert"
          aria-live="polite"
          className={cn(
            "mt-2 flex h-4 items-center gap-1",
            showSelectionError ? "opacity-100" : "opacity-0",
          )}
        >
          <IconExclamationCircle
            className="text-error"
            width={18}
            height={18}
            strokeWidth={2}
            aria-hidden="true"
          />
          <p className="text-sm font-semibold text-error">
            Select an image to edit
          </p>
        </div>
        <ImagePromptInput
          mainView={mainView}
          setMainView={setMainView}
          isSelectionConfirmed={isSelectionConfirmed}
          onMissingSelection={() => setShowSelectionError(true)}
          onGenerated={() => setIsSelectionConfirmed(false)}
        />
      </div>
    </motion.div>
  );
}

function ImagePromptInput({
  mainView,
  setMainView,
  isSelectionConfirmed,
  onMissingSelection,
  onGenerated,
}: {
  mainView: ViewStateOf<MainViewState, "EDIT_IMAGE">;
  setMainView: SetView<MainViewState>;
  isSelectionConfirmed: boolean;
  onMissingSelection: () => void;
  onGenerated: () => void;
}) {
  const isStartImage = mainView.sourceImageKey === "startSourceImageExternalId";
  const dynamicFormKey = isStartImage
    ? "startImageEditForm"
    : "endImageEditForm";

  const imageForm = useEditorStore(
    (state) => state.service.getCurrentCreativeProjectState()?.[dynamicFormKey],
  );

  const prompt = imageForm?.prompt ?? "";

  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );

  const elementExternalIds = useEditorStore(
    (state) =>
      state.service.getCurrentCreativeProjectState()?.[dynamicFormKey]
        ?.elementExternalIds,
  );

  const { updateCurrentCreativeProjectState, createCustomInputSourceImage } =
    useEditorStore((state) => state.service);

  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const { data: team } = useTeamQuery(teamExternalId);

  const credits = calculateCreativeImageEditCredits(
    team?.billingInfo.customPricing ?? null,
  );

  const handleSubmit = async () => {
    if (!prompt.trim() || !timelineItem?.externalId) return;
    if (!isSelectionConfirmed) {
      onMissingSelection();
      return;
    }
    try {
      await createCustomInputSourceImage(mainView.sourceImageKey, {
        customInput: { prompt: prompt.trim() },
        elementExternalIds: elementExternalIds ?? [],
      });
      AnalyticsApi.logCustomImageEdit();
      queryClient.invalidateQueries({
        queryKey: sourceImagePresetQueryKeys.all(),
      });
      emitInspirationAction(ActionIdentifier.GENERATE_IMAGE);
      onGenerated();
      Toast.infoWhite("Image generation started");
    } catch (e: any) {
      if (e?.response?.data?.errorCode === "NOT_ENOUGH_CREDITS") {
        return;
      }
      console.error(e);
      Toast.error("Failed to generate image");
    }
  };

  return (
    <div className="mt-auto flex flex-col">
      <div className="mb-3 flex h-15 items-end">
        <p className="mx-1.5 flex-1 text-xl font-semibold text-text-primary">
          Edit image
        </p>
        <SelectedElements
          mainView={mainView}
          dynamicFormKey={dynamicFormKey}
          setMainView={setMainView}
        />
      </div>

      <GradientPromptTextarea
        value={prompt}
        onChange={(value) =>
          updateCurrentCreativeProjectState((draft) => {
            if (draft[dynamicFormKey]) {
              draft[dynamicFormKey].prompt = value;
            }
          })
        }
        submitLabel={
          <div className="flex items-center">
            <span>Generate image</span>
            <CreditsText
              credits={credits}
              isVisible={
                (team?.billingInfo.showCredits ?? false) &&
                (team?.billingInfo?.creditBalance ?? null) !== null
              }
            />
          </div>
        }
        onSubmit={handleSubmit}
        source="EDIT_IMAGE"
        placeholder="Write a prompt to edit the selected image..."
        actionsChildren={
          <>
            <Button
              size="sm"
              variant="quaternary"
              icon={<IconLibrary width={24} height={24} strokeWidth={2.5} />}
              iconPosition="leading"
              className="pl-3"
              onClick={() =>
                setMainView({
                  mode: "PROMPT_LIBRARY",
                  source: "EDIT_IMAGE",
                  sourceImageKey: mainView.sourceImageKey,
                })
              }
            >
              Recent prompts
            </Button>
          </>
        }
      />
    </div>
  );
}

function SourceImageSelector({
  mainView,
  sourceImagesFromTimelineItem,
  onSelect,
  isSelectionPending,
}: {
  mainView: ViewStateOf<MainViewState, "EDIT_IMAGE">;
  sourceImagesFromTimelineItem: SourceImageDto[];
  onSelect: () => void;
  isSelectionPending: boolean;
}) {
  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const sourceImagesFromEditorStore = useEditorStore(
    (state) => state.sourceImages,
  );

  const { replaceCurrentTimelineItemSourceImage } = useEditorStore(
    (state) => state.service,
  );

  const sourceExternalId = timelineItem?.[mainView.sourceImageKey];
  const selectedSourceImage = sourceExternalId
    ? sourceImagesFromEditorStore[sourceExternalId]
    : null;

  return (
    <HorizontalList className="mt-1.5 gap-1">
      {sourceImagesFromTimelineItem.map((sourceImage, index) => {
        const isSelected =
          selectedSourceImage?.externalId === sourceImage.externalId;
        const isProcessing = isSourceImageProcessing(sourceImage);

        return (
          <button
            key={index}
            className={cn(
              "relative h-12 max-h-12 max-w-12 min-w-12 cursor-pointer overflow-hidden rounded-sm border-2 p-px",
              isSelected ? "border-tertiary-400" : "border-transparent",
              isSelected && isSelectionPending && "border-dashed",
            )}
            onClick={() => {
              if (!sourceImage) return;

              onSelect();
              replaceCurrentTimelineItemSourceImage(
                mainView.sourceImageKey,
                sourceImage,
              );
            }}
          >
            <img
              src={sourceImage?.thumbnailSrc}
              alt={`Thumbnail ${index + 1}`}
              className={cn(
                "h-full w-full rounded object-cover transition-opacity",
                isSelected ? "opacity-100" : "opacity-50",
              )}
            />
            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <IconLoader2 size={16} className="animate-spin text-white" />
              </div>
            )}
          </button>
        );
      })}
    </HorizontalList>
  );
}

function SelectedElements({
  mainView,
  dynamicFormKey,
  setMainView,
}: {
  mainView: ViewStateOf<MainViewState, "EDIT_IMAGE">;
  dynamicFormKey: "startImageEditForm" | "endImageEditForm";
  setMainView: SetView<MainViewState>;
}) {
  const { updateCurrentCreativeProjectState } = useEditorStore(
    (state) => state.service,
  );

  const elementExternalIds = useEditorStore(
    (state) =>
      state.service.getCurrentCreativeProjectState()?.[dynamicFormKey]
        ?.elementExternalIds,
  );
  const creativeElements = useEditorStore((state) => state.creativeElements);
  const selectedElements = (elementExternalIds ?? [])
    .map((id) => creativeElements[id])
    .filter((el): el is CreativeElementDto => !!el);

  const handleAddElements = () => {
    setMainView({
      mode: "ELEMENTS",
      sourceImageKey: mainView.sourceImageKey,
    });
  };

  const removeElement = useCallback(
    (elementExternalId: string) => {
      updateCurrentCreativeProjectState((draft) => {
        const index =
          draft[dynamicFormKey]?.elementExternalIds.indexOf(
            elementExternalId,
          ) ?? -1;

        if (index === -1) return;

        draft[dynamicFormKey].elementExternalIds.splice(index, 1);
      });
    },
    [updateCurrentCreativeProjectState, dynamicFormKey],
  );

  if (selectedElements.length === 0) {
    return (
      <Button
        variant="quaternary"
        size="sm"
        className="pl-3"
        icon={
          <img
            src="/inspiration-circles.png"
            alt="Add elements"
            width={56}
            height={27}
          />
        }
        iconPosition="leading"
        onClick={handleAddElements}
      >
        Add elements
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="iconSm"
        variant="quaternary"
        className="mr-1"
        onClick={handleAddElements}
      >
        <IconPlus size={16} className="text-text-secondary" />
      </Button>
      {selectedElements.map((element) => (
        <div
          key={element.externalId}
          className="group relative h-12 w-12 shrink-0 cursor-pointer rounded-sm border"
          onClick={() => removeElement(element.externalId)}
        >
          <img
            src={element.thumbnailSrc}
            alt="Selected element"
            className="h-full w-full object-contain"
          />
          <div className="absolute inset-0 hidden cursor-pointer items-center justify-center rounded bg-black/50 group-hover:flex">
            <IconTrash size={16} className="text-white" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default EditImageView;
